import { call, put } from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import i18n from 'i18next';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { ApiResponse } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import { fillTifdWorkbook } from '../api';
import { fillTifdWorkbookFailure, fillTifdWorkbookSuccess } from '../actions';
import { FillTifdWorkbookRequestAction } from '../types';

const downloadName = (
  response: Response,
  periodStart?: string,
  periodEnd?: string,
  reportId?: string
): string => {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(disposition);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].trim());
    } catch {
      return match[1].trim();
    }
  }
  const period =
    periodStart && periodEnd ? `${periodStart}-${periodEnd}` : reportId;
  return `MTCA-workbook-${period}.xlsx`;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fillTifdWorkbookSaga(action: FillTifdWorkbookRequestAction) {
  const { applicationConnectionId, reportId, file, periodStart, periodEnd } =
    action.payload;
  try {
    const response: Response = yield call(
      fillTifdWorkbook,
      applicationConnectionId,
      reportId,
      file
    );
    if (!response) {
      throw new Error(i18n.t('tifd-report.workbook-failed'));
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      const payload: ApiResponse = yield call([response, 'json']);
      const err = checkResponse(payload?.responseMessage);
      if (err) throw err;
      throw new Error(
        payload?.responseMessage?.message || i18n.t('tifd-report.workbook-failed')
      );
    }

    const blob: Blob = yield call([response, 'blob']);
    saveAs(blob, downloadName(response, periodStart, periodEnd, reportId));
    yield put(fillTifdWorkbookSuccess());
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    yield put(
      turnMessageOn(
        message ?? {
          title: i18n.t('common.error'),
          description: i18n.t('tifd-report.workbook-failed'),
          type: MessageStates.ERROR,
        }
      )
    );
    yield put(fillTifdWorkbookFailure());
  }
}
